import i18n from 'i18next';
import type { ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';

export type SupportedLanguage = 'pt' | 'en' | 'es';

/**
 * Os três idiomas, cada um no seu pedaço.
 *
 * ⚠️ **Eram três `import` estáticos, e os três iam no chunk de entrada.** São
 * ~126 kB de JSON cru, dos quais um usuário brasileiro baixava ~84 kB de inglês
 * e espanhol que nunca ia ler — antes de a primeira palavra da página aparecer.
 * Conferido na época: as 270 strings longas do `es.json` estavam todas no bundle
 * inicial.
 *
 * O `import()` dentro da função é o que faz o bundler separar. Um objeto com os
 * três `import()` já resolvidos não adiantaria nada: o que importa é a chamada
 * acontecer só quando o idioma é escolhido.
 */
const LOADERS: Record<SupportedLanguage, () => Promise<{ default: ResourceKey }>> = {
  pt: () => import('@core/locales/pt.json'),
  en: () => import('@core/locales/en.json'),
  es: () => import('@core/locales/es.json'),
};

/**
 * Garante que o idioma esteja carregado, sem baixar duas vezes.
 *
 * `hasResourceBundle` é a pergunta certa: quem já trocou de idioma e voltou não
 * pode pagar a rede de novo, e um `Set` nosso de "já carregados" seria uma
 * segunda fonte de verdade sobre o que o i18next tem na mão.
 */
async function ensureBundle(lang: SupportedLanguage): Promise<void> {
  if (i18n.hasResourceBundle(lang, 'translation')) return;
  const mod = await LOADERS[lang]();
  i18n.addResourceBundle(lang, 'translation', mod.default, true, true);
}

export const SUPPORTED_LANGUAGES: { code: SupportedLanguage; label: string; flag: string }[] = [
  { code: 'pt', label: 'Português', flag: '🇧🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
];

const STORAGE_KEY = 'balancedsquad.language';

function isSupported(value: string | null | undefined): value is SupportedLanguage {
  return value === 'pt' || value === 'en' || value === 'es';
}

// Equivalente web do getDeviceLanguage() do mobile, que lia expo-localization.
// navigator.languages já vem na ordem de preferência do usuário; a primeira que
// reconhecemos vence. Sem correspondência, cai em inglês — a mesma regra do
// mobile, onde só pt e es são detectados explicitamente.
export function getBrowserLanguage(): SupportedLanguage {
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = tag.toLowerCase().split('-')[0];
    if (base === 'pt' || base === 'es' || base === 'en') return base;
  }
  return 'en';
}

export function getStoredLanguage(): SupportedLanguage | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isSupported(saved) ? saved : null;
  } catch {
    // localStorage indisponível (modo privado em alguns navegadores, iframe
    // com cookies bloqueados). Não é motivo para derrubar o app.
    return null;
  }
}

export function setStoredLanguage(lang: SupportedLanguage): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // idem: preferência não persiste, mas a sessão atual continua.
  }
}

/**
 * Troca o idioma, buscando-o antes se for a primeira vez.
 *
 * ⚠️ **A ordem importa:** carregar ANTES de trocar. Ao contrário, o i18next
 * passaria a apontar para um idioma sem recursos e a tela renderizaria as chaves
 * cruas ("shell.tagline") até o arquivo chegar — pior que a espera, porque
 * parece defeito.
 *
 * A preferência é gravada primeiro, e de propósito: se a rede cair no meio, a
 * escolha da pessoa sobrevive e vale no próximo carregamento.
 */
export async function changeLanguage(lang: SupportedLanguage): Promise<void> {
  setStoredLanguage(lang);
  await ensureBundle(lang);
  await i18n.changeLanguage(lang);
  document.documentElement.lang = lang;
}

export async function initI18n(): Promise<void> {
  if (i18n.isInitialized) return;

  const lng = getStoredLanguage() ?? getBrowserLanguage();
  const mod = await LOADERS[lng]();

  await i18n.use(initReactI18next).init({
    // Só o idioma ativo. Os outros dois entram por `ensureBundle` quando (e se)
    // alguém trocar.
    resources: { [lng]: { translation: mod.default } },
    lng,
    // ⚠️ O fallback fica no PRÓPRIO idioma, e não num "pt" fixo.
    //
    // Com "pt" fixo, o i18next procuraria um bundle que agora pode não estar
    // carregado — e uma chave faltando cairia num idioma ausente em vez de
    // aparecer. O resultado seria o mesmo (a chave crua na tela), mas por um
    // caminho que parece ter uma rede de segurança e não tem.
    //
    // Quem garante que não falta chave é o teste de paridade dos locales, que
    // exige as mesmas chaves nos três — é ele a rede de segurança, e ela roda
    // no CI, não no navegador do usuário.
    fallbackLng: lng,
    // React já escapa o que renderiza; escapar de novo mostraria &quot; na tela.
    interpolation: { escapeValue: false },
  });

  document.documentElement.lang = lng;
}

export default i18n;
